import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { useNotify } from 'react-admin';

import {
    disconnectQuickBooks,
    fetchQuickBooksConnect,
    fetchQuickBooksStatus,
    syncQuickBooksProjects,
    type QuickBooksStatus,
} from './receiptApi';

const EMPTY: QuickBooksStatus = {
    configured: false,
    connected: false,
    environment: 'sandbox',
    realm_id: null,
    company_name: null,
    connected_at: null,
    warning: null,
    redirect_uri: null,
    credit_card_accounts: [],
};

export const QuickBooksConnectCard = () => {
    const notify = useNotify();
    const [params, setParams] = useSearchParams();
    const [status, setStatus] = useState<QuickBooksStatus>(EMPTY);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        setStatus(await fetchQuickBooksStatus());
    };

    useEffect(() => {
        void refresh().catch(error =>
            notify(error instanceof Error ? error.message : 'QuickBooks status failed', {
                type: 'error',
            })
        );
    }, [notify]);

    useEffect(() => {
        const result = params.get('qbo');
        if (!result) return;
        if (result === 'connected') {
            notify('QuickBooks connected', { type: 'success' });
            void refresh().catch(() => undefined);
        } else {
            notify(params.get('detail') || 'QuickBooks connect failed', { type: 'error' });
        }
        params.delete('qbo');
        params.delete('detail');
        setParams(params, { replace: true });
    }, [notify, params, setParams]);

    const connect = async () => {
        setBusy(true);
        try {
            const { authorize_url } = await fetchQuickBooksConnect();
            window.location.assign(authorize_url);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Could not start QuickBooks connect', {
                type: 'error',
            });
            setBusy(false);
        }
    };

    const disconnect = async () => {
        setBusy(true);
        try {
            setStatus(await disconnectQuickBooks());
            notify('QuickBooks disconnected', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Disconnect failed', {
                type: 'error',
            });
        } finally {
            setBusy(false);
        }
    };

    const syncJobs = async () => {
        setBusy(true);
        try {
            const result = await syncQuickBooksProjects();
            notify(
                `Matched Classes · linked ${result.linked}, already ${result.unchanged}, unmatched ${result.unmatched}, accounts ${result.expense_accounts}`,
                { type: 'success' }
            );
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Job sync failed', {
                type: 'error',
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">QuickBooks Online</Typography>
                <Chip
                    size="small"
                    label={status.connected ? 'Connected' : 'Not connected'}
                    color={status.connected ? 'success' : 'default'}
                />
                <Chip size="small" label={status.environment} variant="outlined" />
            </Stack>
            {!status.configured && (
                <Alert severity="warning">
                    Add QBO_CLIENT_ID, QBO_CLIENT_SECRET, and the redirect URI in
                    the backend .env, then restart the API.
                </Alert>
            )}
            {status.configured && !status.connected && status.redirect_uri && (
                <Alert severity="info">
                    In Intuit Developer → MRT → Keys and credentials →
                    Development, add this exact Redirect URI:{' '}
                    <code>{status.redirect_uri}</code>
                </Alert>
            )}
            {status.connected && (
                <Typography color="text.secondary">
                    {status.company_name || 'QuickBooks company'}
                    {status.credit_card_accounts.length
                        ? ` · cards: ${status.credit_card_accounts
                              .map(account => account.name)
                              .join(', ')}`
                        : ''}
                </Typography>
            )}
            {status.warning && <Alert severity="warning">{status.warning}</Alert>}
            <Stack direction="row" spacing={2}>
                <Button
                    variant="contained"
                    disabled={busy || !status.configured}
                    onClick={() => void connect()}
                >
                    {status.connected ? 'Reconnect' : 'Connect QuickBooks'}
                </Button>
                {status.connected && (
                    <Button
                        color="inherit"
                        disabled={busy}
                        onClick={() => void disconnect()}
                    >
                        Disconnect
                    </Button>
                )}
                {status.connected && (
                    <Button
                        variant="outlined"
                        disabled={busy}
                        onClick={() => void syncJobs()}
                    >
                        Match jobs to Classes
                    </Button>
                )}
            </Stack>
        </Stack>
    );
};
